import { useEffect, useMemo, useState } from "react";
import { useProjects } from "@/hooks/admin/useAdminData";
import { useCustomers } from "@/hooks/admin/useAdminData";

type Props = {
  open: boolean;
  onClose: () => void;
  onSent: (count: number, channel: string) => void;
};

type Recipient = {
  customerId: string;
  name: string;
  initials: string;
  color: string;
  email: string;
  phone: string;
  projectId: string;
  moveType: string;
  date: string;
  hasEmail: boolean;
  hasPhone: boolean;
};

const defaultEmailSubject = "How did we do, {{name}}? Share a quick review";
const defaultEmailBody = `Hi {{name}},

Thanks again for choosing Haulmark Movers for your recent {{moveType}}. It was a pleasure working with you!

If you have a moment, we'd love your honest feedback. A quick review helps our crew (and future neighbors moving in your area).

⭐ Leave a 30-second review:
https://haulmark.co/r/{{projectId}}

Thank you again — it truly means the world.

— Elena & the Haulmark team`;

const defaultSmsBody = `Hi {{name}}, it's Haulmark Movers! Hope your move to your new place is going great. If you have 30 seconds, we'd love a quick review: https://haulmark.co/r/{{projectId}} — thank you!`;

export default function RequestReviewModal({ open, onClose, onSent }: Props) {
  const { data: projects, loading: projectsLoading } = useProjects();
  const { data: customers } = useCustomers();

  const [search, setSearch] = useState("");
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [channel, setChannel] = useState<"email" | "sms" | "both">("email");
  const [emailSubject, setEmailSubject] = useState(defaultEmailSubject);
  const [emailBody, setEmailBody] = useState(defaultEmailBody);
  const [smsBody, setSmsBody] = useState(defaultSmsBody);
  const [editing, setEditing] = useState(false);
  const [sending, setSending] = useState(false);

  // Build recipients from completed projects
  const recipients = useMemo<Recipient[]>(() => {
    const completed = projects.filter((p) => p.status === "completed");
    return completed.map((p) => {
      const c = customers.find((cu) => cu.name === p.customer);
      return {
        customerId: c?.id || p.id,
        name: p.customer,
        initials: p.customerInitials,
        color: p.customerColor,
        email: c?.email || `${p.customer.toLowerCase().replace(/[^a-z]/g, ".")}@email.com`,
        phone: c?.phone || "",
        projectId: p.id,
        moveType: p.moveType,
        date: p.date,
        hasEmail: !!c?.email,
        hasPhone: !!c?.phone,
      };
    });
  }, [projects, customers]);

  const filtered = useMemo(() => {
    if (!search.trim()) return recipients;
    const q = search.toLowerCase();
    return recipients.filter(
      (r) =>
        r.name.toLowerCase().includes(q) ||
        r.projectId.toLowerCase().includes(q) ||
        r.email.toLowerCase().includes(q),
    );
  }, [recipients, search]);

  const allSelected = filtered.length > 0 && filtered.every((r) => selectedIds.has(r.projectId));

  // Reset state when reopening
  useEffect(() => {
    if (open) {
      setSearch("");
      setSelectedIds(new Set());
      setChannel("email");
      setEmailSubject(defaultEmailSubject);
      setEmailBody(defaultEmailBody);
      setSmsBody(defaultSmsBody);
      setEditing(false);
      setSending(false);
    }
  }, [open]);

  // Esc to close
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape" && open) onClose();
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [open, onClose]);

  if (!open) return null;

  const toggleOne = (projectId: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(projectId)) next.delete(projectId);
      else next.add(projectId);
      return next;
    });
  };

  const toggleAll = () => {
    if (allSelected) {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        filtered.forEach((r) => next.delete(r.projectId));
        return next;
      });
    } else {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        filtered.forEach((r) => next.add(r.projectId));
        return next;
      });
    }
  };

  const selectedRecipients = recipients.filter((r) => selectedIds.has(r.projectId));
  const channelLabel =
    channel === "both" ? "Email + SMS" : channel === "email" ? "Email" : "SMS";

  const handleSend = async () => {
    if (selectedRecipients.length === 0) return;
    setSending(true);
    // Simulate send latency
    await new Promise((resolve) => setTimeout(resolve, 900));
    onSent(selectedRecipients.length, channelLabel);
    setSending(false);
    onClose();
  };

  return (
    <>
      <div
        className="fixed inset-0 z-40 bg-ink-900/50 backdrop-blur-sm"
        onClick={onClose}
      />
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none">
        <div
          className="bg-white w-full max-w-4xl rounded-lg border border-ink-100 max-h-[90vh] flex flex-col pointer-events-auto"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="px-6 py-4 border-b border-ink-100 flex items-center justify-between shrink-0">
            <div className="flex items-center gap-3">
              <span className="w-10 h-10 flex items-center justify-center rounded-md bg-brand-100 text-brand-700">
                <i className="ri-mail-send-line text-lg"></i>
              </span>
              <div>
                <h2 className="font-display text-lg font-bold text-ink-900">Request Reviews</h2>
                <div className="text-xs text-ink-500">
                  Ask recent completed customers for a quick rating — sent via {channelLabel.toLowerCase()}
                </div>
              </div>
            </div>
            <button
              onClick={onClose}
              className="w-8 h-8 flex items-center justify-center rounded-md hover:bg-cream-100 text-ink-500 cursor-pointer"
              aria-label="Close"
            >
              <i className="ri-close-line text-lg"></i>
            </button>
          </div>

          {/* Body */}
          <div className="flex-1 overflow-hidden flex flex-col md:flex-row">
            {/* Left — recipients */}
            <div className="md:w-3/5 flex flex-col border-r border-ink-100 min-h-0">
              {/* Search + channel */}
              <div className="px-5 pt-4 pb-3 space-y-3 shrink-0">
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-ink-400">
                    <i className="ri-search-line text-sm"></i>
                  </span>
                  <input
                    type="text"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search completed customers by name or project…"
                    className="w-full text-sm pl-10 pr-3 py-2.5 rounded-md bg-cream-50 border border-ink-100 focus:bg-white focus:border-ink-300 outline-none transition-colors"
                  />
                </div>

                <div className="flex items-center gap-2 flex-wrap">
                  {(["email", "sms", "both"] as const).map((c) => (
                    <button
                      key={c}
                      onClick={() => setChannel(c)}
                      className={`text-xs font-semibold px-3 py-1.5 rounded-full cursor-pointer whitespace-nowrap inline-flex items-center gap-1.5 transition-colors ${
                        channel === c
                          ? "bg-ink-900 text-white"
                          : "bg-cream-100 text-ink-600 hover:bg-ink-100"
                      }`}
                    >
                      <i
                        className={
                          c === "email"
                            ? "ri-mail-line"
                            : c === "sms"
                              ? "ri-message-2-line"
                              : "ri-send-plane-line"
                        }
                      ></i>
                      {c === "email" ? "Email" : c === "sms" ? "SMS" : "Email + SMS"}
                    </button>
                  ))}
                </div>
              </div>

              {/* Select all */}
              <div className="px-5 pb-2 flex items-center justify-between shrink-0">
                <label className="flex items-center gap-2 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={allSelected}
                    onChange={toggleAll}
                    className="w-4 h-4 rounded border-ink-300 text-brand-500 cursor-pointer"
                  />
                  <span className="text-xs font-semibold text-ink-700">
                    {allSelected ? "Deselect all" : "Select all"}
                    <span className="ml-1 text-ink-400 font-normal">
                      ({filtered.length} shown)
                    </span>
                  </span>
                </label>
                <span className="text-xs font-bold text-brand-700">
                  {selectedIds.size} selected
                </span>
              </div>

              {/* List */}
              <div className="flex-1 overflow-y-auto scrollbar-thin px-3 pb-3">
                {projectsLoading ? (
                  <div className="text-center text-xs text-ink-400 py-12">
                    <i className="ri-loader-4-line animate-spin text-2xl block mb-2"></i>
                    Loading customers…
                  </div>
                ) : filtered.length === 0 ? (
                  <div className="text-center text-xs text-ink-400 py-12">
                    <i className="ri-user-search-line text-2xl block mb-2"></i>
                    No completed customers match your search.
                  </div>
                ) : (
                  <ul className="space-y-1.5">
                    {filtered.map((r) => {
                      const checked = selectedIds.has(r.projectId);
                      const channelOk =
                        channel === "email"
                          ? r.hasEmail
                          : channel === "sms"
                            ? r.hasPhone
                            : r.hasEmail || r.hasPhone;
                      return (
                        <li key={r.projectId}>
                          <label
                            className={`flex items-center gap-3 px-3 py-2.5 rounded-md cursor-pointer transition-colors border ${
                              checked
                                ? "bg-brand-50 border-brand-200"
                                : "bg-white border-ink-100 hover:bg-cream-50"
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => toggleOne(r.projectId)}
                              className="w-4 h-4 rounded border-ink-300 text-brand-500 cursor-pointer"
                            />
                            <span
                              className={`w-9 h-9 rounded-full ${r.color} text-white text-[11px] font-bold flex items-center justify-center shrink-0`}
                            >
                              {r.initials}
                            </span>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="text-sm font-semibold text-ink-900 truncate">
                                  {r.name}
                                </span>
                                <span className="text-[10px] font-mono font-bold text-brand-700">
                                  {r.projectId}
                                </span>
                              </div>
                              <div className="text-[11px] text-ink-500 truncate flex items-center gap-1.5 mt-0.5">
                                <span className="truncate">{r.moveType}</span>
                                <span>·</span>
                                <span className="whitespace-nowrap">{r.date}</span>
                              </div>
                              <div className="text-[11px] text-ink-400 truncate mt-0.5 flex items-center gap-2">
                                {r.hasEmail && (
                                  <span className="inline-flex items-center gap-1">
                                    <i className="ri-mail-line"></i>
                                    {r.email}
                                  </span>
                                )}
                                {r.hasPhone && (
                                  <span className="inline-flex items-center gap-1">
                                    <i className="ri-phone-line"></i>
                                    {r.phone}
                                  </span>
                                )}
                              </div>
                            </div>
                            {!channelOk && (
                              <span className="text-[10px] font-bold text-amber-700 bg-amber-50 px-1.5 py-0.5 rounded whitespace-nowrap">
                                No {channel === "email" ? "email" : "phone"}
                              </span>
                            )}
                          </label>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            </div>

            {/* Right — preview / template */}
            <div className="md:w-2/5 flex flex-col min-h-0 bg-cream-50">
              <div className="px-5 pt-4 pb-2 shrink-0 flex items-center justify-between">
                <div>
                  <div className="text-[10px] font-bold uppercase tracking-wider text-ink-500">
                    Message preview
                  </div>
                  <div className="text-xs text-ink-700 mt-0.5">
                    Variables auto-filled per recipient
                  </div>
                </div>
                <button
                  onClick={() => setEditing((v) => !v)}
                  className="text-[11px] font-semibold text-brand-700 hover:underline cursor-pointer inline-flex items-center gap-1"
                >
                  <i className={editing ? "ri-eye-line" : "ri-edit-line"}></i>
                  {editing ? "Preview" : "Edit"}
                </button>
              </div>

              <div className="flex-1 overflow-y-auto scrollbar-thin px-5 pb-4 space-y-3">
                {(channel === "email" || channel === "both") && (
                  <div className="bg-white border border-ink-100 rounded-md overflow-hidden">
                    <div className="px-3 py-2 bg-cream-100 border-b border-ink-100 text-[10px] font-bold uppercase tracking-wider text-ink-500 flex items-center gap-1.5">
                      <i className="ri-mail-line"></i>
                      Email
                    </div>
                    <div className="p-3 space-y-2">
                      <div>
                        <div className="text-[10px] font-bold text-ink-400 mb-1">SUBJECT</div>
                        {editing ? (
                          <input
                            type="text"
                            value={emailSubject}
                            onChange={(e) => setEmailSubject(e.target.value)}
                            className="w-full text-sm font-semibold text-ink-900 border border-ink-200 rounded px-2 py-1.5 outline-none focus:border-ink-400"
                          />
                        ) : (
                          <div className="text-sm font-semibold text-ink-900">
                            {previewVars(emailSubject, selectedRecipients[0])}
                          </div>
                        )}
                      </div>
                      <div>
                        <div className="text-[10px] font-bold text-ink-400 mb-1">BODY</div>
                        {editing ? (
                          <textarea
                            value={emailBody}
                            onChange={(e) => setEmailBody(e.target.value.slice(0, 500))}
                            rows={9}
                            className="w-full text-[12px] text-ink-700 border border-ink-200 rounded px-2 py-1.5 outline-none focus:border-ink-400 resize-none leading-relaxed"
                          />
                        ) : (
                          <div className="text-[12px] text-ink-700 leading-relaxed whitespace-pre-wrap">
                            {previewVars(emailBody, selectedRecipients[0])}
                          </div>
                        )}
                        {editing && (
                          <div className="text-[10px] text-ink-400 mt-1 text-right">
                            {emailBody.length}/500
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {(channel === "sms" || channel === "both") && (
                  <div className="bg-white border border-ink-100 rounded-md overflow-hidden">
                    <div className="px-3 py-2 bg-cream-100 border-b border-ink-100 text-[10px] font-bold uppercase tracking-wider text-ink-500 flex items-center gap-1.5">
                      <i className="ri-message-2-line"></i>
                      SMS
                    </div>
                    <div className="p-3">
                      {editing ? (
                        <textarea
                          value={smsBody}
                          onChange={(e) => setSmsBody(e.target.value.slice(0, 320))}
                          rows={5}
                          className="w-full text-[12px] text-ink-700 border border-ink-200 rounded px-2 py-1.5 outline-none focus:border-ink-400 resize-none leading-relaxed"
                        />
                      ) : (
                        <div className="text-[12px] text-ink-700 leading-relaxed whitespace-pre-wrap">
                          {previewVars(smsBody, selectedRecipients[0])}
                        </div>
                      )}
                      {editing && (
                        <div className="text-[10px] text-ink-400 mt-1 text-right">
                          {smsBody.length}/320
                        </div>
                      )}
                    </div>
                  </div>
                )}

                <div className="bg-white border border-ink-100 rounded-md p-3">
                  <div className="text-[10px] font-bold uppercase tracking-wider text-ink-500 mb-2">
                    Available variables
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {[
                      { v: "{{name}}", d: "Customer name" },
                      { v: "{{projectId}}", d: "Move ID" },
                      { v: "{{moveType}}", d: "Move type" },
                    ].map((x) => (
                      <span
                        key={x.v}
                        className="text-[10px] font-mono bg-cream-100 text-ink-700 px-1.5 py-0.5 rounded"
                        title={x.d}
                      >
                        {x.v}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-ink-100 bg-cream-50 flex items-center justify-between gap-2 shrink-0 rounded-b-lg">
            <div className="text-xs text-ink-600">
              {selectedIds.size > 0 ? (
                <span>
                  Sending <strong className="text-ink-900">{channelLabel}</strong> to{" "}
                  <strong className="text-ink-900">{selectedIds.size}</strong>{" "}
                  {selectedIds.size === 1 ? "customer" : "customers"}
                </span>
              ) : (
                <span className="text-ink-400">Select at least one customer to continue</span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={onClose}
                className="text-sm font-semibold text-ink-500 hover:text-ink-900 px-3 py-2 cursor-pointer"
              >
                Cancel
              </button>
              <button
                onClick={handleSend}
                disabled={selectedIds.size === 0 || sending}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md text-sm font-semibold bg-brand-500 hover:bg-brand-600 text-white cursor-pointer whitespace-nowrap disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {sending ? (
                  <>
                    <i className="ri-loader-4-line animate-spin"></i>
                    Sending…
                  </>
                ) : (
                  <>
                    <i className="ri-send-plane-fill"></i>
                    Send {selectedIds.size > 0 ? `(${selectedIds.size})` : ""}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

function previewVars(template: string, r?: Recipient) {
  if (!r) {
    return template
      .replace(/\{\{name\}\}/g, "Margaret")
      .replace(/\{\{projectId\}\}/g, "MV-2039")
      .replace(/\{\{moveType\}\}/g, "Apartment — 2 BR");
  }
  const firstName = r.name.split(/\s+/)[0];
  return template
    .replace(/\{\{name\}\}/g, firstName)
    .replace(/\{\{projectId\}\}/g, r.projectId)
    .replace(/\{\{moveType\}\}/g, r.moveType);
}